import React, { useState, useEffect, useCallback } from 'react';
import {
  History, RefreshCw, X, Receipt, Filter,
  ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight,
} from 'lucide-react';
import billService from '@/services/billService';

/* actionType is fixed for this view: always sent to the backend, never shown in the UI */
const DEFAULT_ACTION_TYPE = 'BILL_CANCEL';

/* ---- sample (matches your API shape) — used only when useMock is true ---- */
const SAMPLE = {
  totalRecords: 1,
  data: [
    {
      id: 5,
      actionType: 'BILL_CANCEL',
      details: {
        billId: 29, billNumber: 'INV/2025-26-00029', billStatus: 'closed',
        totalItems: 4, finalAmount: 1710, tableNumber: 'H-01',
        businessDate: '2026-06-02', customerName: 'Anas Sanderwale', paymentStatus: 'completed',
        reason: 'Duplicate bill — same order was billed twice',
        restoredStock: [
          { inventoryItemId: 12, itemName: 'Paneer', quantity: 0.4, stockBefore: 10, stockAfter: 10.4 },
          { inventoryItemId: 18, itemName: 'Butter', quantity: 0.15, stockBefore: 4, stockAfter: 4.15 },
        ],
      },
      branchId: 'dd756cd9-0dc7-4346-a8a2-a99faeb4226c',
      performedBy: 'luffy',
      performedAt: '2026-06-02T14:17:47.108792',
    },
  ],
};

/* ---- action type -> colored badge (for the table cell) ---- */
const ACTION_STYLES = {
  BILL_CANCEL:         'bg-red-100 text-red-700',
  BILL_REPRINT:        'bg-blue-100 text-blue-700',
  PAYMENT_MODE_CHANGE: 'bg-amber-100 text-amber-700',
  NC_BILL:             'bg-purple-100 text-purple-700',
};
const actionLabel = (t) => String(t || '').replace(/_/g, ' ');
const actionStyle = (t) => ACTION_STYLES[t] || 'bg-gray-100 text-gray-700';

const statusBadge = (value) => {
  const v = String(value || '').toLowerCase();
  const map = {
    closed:    'bg-gray-100 text-gray-700',
    completed: 'bg-emerald-100 text-emerald-700',
    pending:   'bg-amber-100 text-amber-700',
    cancelled: 'bg-red-100 text-red-700',
    failed:    'bg-red-100 text-red-700',
  };
  return map[v] || 'bg-gray-100 text-gray-700';
};

const money = (n) =>
  typeof n === 'number'
    ? `\u20B9${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : n ?? '\u2014';

/* ---- "2026-06-02T14:17:47.108792" -> "02 Jun 2026, 2:17 PM" ---- */
const formatDateTime = (iso) => {
  if (!iso) return '\u2014';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ---- pull { totalRecords, data } out regardless of whether apiClient unwraps .data ---- */
const unwrap = (res) => {
  if (res?.totalRecords !== undefined) return res;             // apiClient already unwrapped
  if (res?.data?.totalRecords !== undefined) return res.data;  // raw axios response
  return res ?? {};
};

const EMPTY_FILTERS = {
  performedBy: '',
  performedFromDate: '',
  performedToDate: '',
};

export default function AuditLogTable({ branchId = null, useMock = false }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [page, setPage] = useState(0);   // zero-based
  const [size, setSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Applied filters (sent to API) vs draft (what's typed in the panel)
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const [selectedDetails, setSelectedDetails] = useState(null);

  const activeCount = Object.values(filters).filter(Boolean).length;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = useMock
        ? SAMPLE
        : await billService.getAuditLogs({
            limit: size,
            offset: page * size,
            branchId,
            actionType: DEFAULT_ACTION_TYPE, // always sent, not user-editable
            ...filters, // empty values are skipped inside the service
          });

      const body = unwrap(res);
      const list = Array.isArray(body.data) ? body.data : [];
      setRows(list);
      setTotalRecords(body.totalRecords ?? list.length);
    } catch (err) {
      console.error('Failed to load audit logs:', err);
      setError('Failed to load audit logs.');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [page, size, branchId, filters, useMock]);

  useEffect(() => { load(); }, [load]);

  // reset to first page when branch filter changes
  useEffect(() => { setPage(0); }, [branchId]);

  // close popup on Escape
  useEffect(() => {
    if (!selectedDetails) return;
    const onKey = (e) => e.key === 'Escape' && setSelectedDetails(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selectedDetails]);

  const toggleFilters = () => {
    if (!filtersOpen) setDraft(filters); // sync draft with what's applied
    setFiltersOpen((o) => !o);
  };

  const applyFilters = () => {
    setFilters(draft);
    setPage(0);
    setFiltersOpen(false);
  };

  const clearFilters = () => {
    setDraft(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const totalPages = Math.max(1, Math.ceil(totalRecords / size));
  const from = totalRecords === 0 ? 0 : page * size + 1;
  const to = Math.min((page + 1) * size, totalRecords);

  const detailRows = selectedDetails && [
    { label: 'Bill Number',    value: selectedDetails.billNumber },
    { label: 'Bill Status',    value: selectedDetails.billStatus, badge: true },
    { label: 'Table No',       value: selectedDetails.tableNumber },
    { label: 'Total Items',    value: selectedDetails.totalItems },
    { label: 'Final Amount',   value: money(selectedDetails.finalAmount), strong: true },
    { label: 'Payment Status', value: selectedDetails.paymentStatus, badge: true },
    { label: 'Customer Name',  value: selectedDetails.customerName },
    { label: 'Business Date',  value: selectedDetails.businessDate },
  ];

  /* Stock put back when the bill was cancelled. Absent on entries logged before
     stock reversal existed, and an empty array when the bill consumed nothing
     (no recipes / no finished items) — both render as no section at all. */
  const restoredStock = Array.isArray(selectedDetails?.restoredStock)
    ? selectedDetails.restoredStock
    : [];

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <button
          onClick={toggleFilters}
          className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${
            filtersOpen || activeCount > 0
              ? 'border-teal-500 text-teal-700 bg-teal-50'
              : 'border-gray-300 text-gray-600 hover:border-teal-400 hover:text-teal-600'
          }`}
        >
          <Filter size={15} />
          Filters
          {activeCount > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-xs font-semibold text-white bg-teal-600 rounded-full">
              {activeCount}
            </span>
          )}
        </button>

        <button
          onClick={load}
          className="inline-flex items-center justify-center w-9 h-9 rounded-lg border border-gray-300 text-gray-500 hover:text-teal-600 hover:border-teal-400 transition-colors"
          title="Refresh"
        >
          <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filter panel */}
      {filtersOpen && (
        <div className="px-4 py-4 border-b border-gray-100 bg-gray-50">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Performed By */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Performed By</label>
              <input
                type="text"
                value={draft.performedBy}
                onChange={(e) => setDraft((d) => ({ ...d, performedBy: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                placeholder="username"
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* From Date */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">From Date</label>
              <input
                type="date"
                value={draft.performedFromDate}
                max={draft.performedToDate || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, performedFromDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* To Date */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">To Date</label>
              <input
                type="date"
                value={draft.performedToDate}
                min={draft.performedFromDate || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, performedToDate: e.target.value }))}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              onClick={clearFilters}
              className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-colors shadow-sm"
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Count row */}
      <div className="px-4 py-2.5 border-b border-gray-100">
        <p className="text-sm text-gray-500">
          Showing <span className="font-medium text-gray-700">{from}</span>&ndash;
          <span className="font-medium text-gray-700">{to}</span> of{' '}
          <span className="font-medium text-gray-700">{totalRecords}</span> logs
        </p>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <th className="px-4 py-3">ID</th>
              <th className="px-4 py-3">Bill Number</th>
              <th className="px-4 py-3">Action Type</th>
              <th className="px-4 py-3">Performed By</th>
              <th className="px-4 py-3">Performed At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-gray-400">Loading&hellip;</td></tr>
            ) : error ? (
              <tr><td colSpan={5} className="px-4 py-16 text-center text-red-500">{error}</td></tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-16 text-center">
                  <div className="flex flex-col items-center gap-2 text-gray-400">
                    <History className="w-8 h-8 text-gray-300" />
                    <p className="font-medium text-gray-500">No audit logs found</p>
                    <p className="text-xs">{activeCount > 0 ? 'Try adjusting your filters' : 'Activity will appear here'}</p>
                  </div>
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-gray-600">{row.id}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => setSelectedDetails(row.details)}
                      className="font-medium text-teal-600 hover:text-teal-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                      disabled={!row.details}
                    >
                      {row.details?.billNumber ?? '\u2014'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${actionStyle(row.actionType)}`}>
                      {actionLabel(row.actionType)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{row.performedBy ?? '\u2014'}</td>
                  <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{formatDateTime(row.performedAt)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Footer / pagination */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100">
        <div className="flex items-center gap-2 text-sm text-gray-500">
          Show
          <select
            value={size}
            onChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
            className="border border-gray-300 rounded-md px-2 py-1 text-sm focus:ring-2 focus:ring-teal-500 focus:border-teal-500 cursor-pointer"
          >
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
          entries
        </div>

        <div className="flex items-center gap-1">
          <PagerBtn onClick={() => setPage(0)} disabled={page === 0}><ChevronsLeft size={16} /></PagerBtn>
          <PagerBtn onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}><ChevronLeft size={16} /></PagerBtn>
          <span className="px-3 py-1 rounded-md bg-teal-600 text-white text-sm font-medium min-w-[32px] text-center">{page + 1}</span>
          <PagerBtn onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}><ChevronRight size={16} /></PagerBtn>
          <PagerBtn onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1}><ChevronsRight size={16} /></PagerBtn>
        </div>
      </div>

      {/* ---- Bill details popup (inline) ---- */}
      {selectedDetails && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
          onClick={() => setSelectedDetails(null)}
        >
          <div
            className="w-full max-w-md bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg flex items-center justify-center shadow-sm">
                  <Receipt className="w-4 h-4 text-white" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-gray-900">Bill Details</h3>
                  <p className="text-xs text-gray-500">{selectedDetails.billNumber}</p>
                </div>
              </div>
              <button
                onClick={() => setSelectedDetails(null)}
                className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scrolls on its own: reason + restored-stock list can make this tall. */}
            <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
              <dl className="divide-y divide-gray-100">
                {detailRows.map((r) => (
                  <div key={r.label} className="flex items-center justify-between py-2.5">
                    <dt className="text-sm text-gray-500">{r.label}</dt>
                    <dd className="text-sm text-right">
                      {r.badge ? (
                        <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${statusBadge(r.value)}`}>
                          {r.value ?? '\u2014'}
                        </span>
                      ) : (
                        <span className={r.strong ? 'font-semibold text-gray-900' : 'text-gray-800'}>
                          {r.value ?? '\u2014'}
                        </span>
                      )}
                    </dd>
                  </div>
                ))}
              </dl>

              {/* Reason — free text, so it gets its own block rather than a right-aligned
                  row that would wrap badly. */}
              {selectedDetails.reason && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-1.5">Reason</p>
                  <p className="text-sm text-gray-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 whitespace-pre-wrap break-words">
                    {selectedDetails.reason}
                  </p>
                </div>
              )}

              {/* What went back into inventory. The bill's stock_updates rows are deleted
                  with the bill, so this is the only record of the reversal. */}
              {restoredStock.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-500 mb-1.5">Stock Restored</p>
                  <ul className="divide-y divide-emerald-100 bg-emerald-50 border border-emerald-100 rounded-lg px-3">
                    {restoredStock.map((s) => (
                      <li key={s.inventoryItemId} className="flex items-center justify-between py-2 gap-3">
                        <span className="text-sm text-gray-800 break-words">
                          {s.itemName ?? `Item #${s.inventoryItemId}`}
                        </span>
                        <span className="text-sm font-semibold text-emerald-700 whitespace-nowrap">
                          +{Number(s.quantity).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex justify-end">
              <button
                onClick={() => setSelectedDetails(null)}
                className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg hover:from-teal-600 hover:to-teal-700 transition-colors shadow-sm"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PagerBtn({ children, disabled, onClick }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
    >
      {children}
    </button>
  );
}